import { DateTime } from "luxon";
import { ObjectType as PaymentObjectType } from "#graphql/supergraphEnums";
import { type CreatePaymentBatchInput } from "#graphql/supergraphTypes";
import { stringToPaymentMethod } from "../../utils";
import { PositionRowData } from "../SelectPositionsStep/SelectPositionsTable/types";

export const paymentProcessDateToISO = (paymentProcessDate: Date): string | null => {
  /**
   * paymentProcessDate corresponds to the local date time picked in the DatePicker.
   * We must convert it to Eastern Time without changing the time (e.g. 9PM PT to 9PM ET) first before using it.
   * Then, we convert the entire date to UTC (the time AND timezone are changed) before sending the value to
   * the Payment Service (since the DB uses UTC).
   */
  const paymentProcessDateET = DateTime.fromJSDate(paymentProcessDate).setZone(
    "America/New_York",
    { keepLocalTime: true },
  );
  return paymentProcessDateET.toUTC().toISO();
};

export const buildCreatePaymentBatchInput = (params: {
  selectedPositions: PositionRowData[];
  paymentDescription: string | undefined;
  distributionBatchId: number;
  scheduledPaymentProcessDate: Date | null;
  makerNote?: string;
}): CreatePaymentBatchInput => {
  const {
    selectedPositions,
    paymentDescription,
    distributionBatchId,
    scheduledPaymentProcessDate,
    makerNote,
  } = params;

  let scheduledPaymentProcessISODate: string | null = null;
  if (scheduledPaymentProcessDate) {
    scheduledPaymentProcessISODate = paymentProcessDateToISO(scheduledPaymentProcessDate);
  }

  const payments: CreatePaymentBatchInput["payments"] = selectedPositions.map((p) => {
    const paymentMethod = stringToPaymentMethod(p.paymentMethod);
    if (!paymentMethod) {
      // should never get here as we shouldn't be creating mutation inputs
      // for positions with undefined payment method
      throw new TypeError("paymentMethod is undefined or null");
    }
    return {
      objectId: p.distributionId,
      objectType: PaymentObjectType.Distribution,
      method: paymentMethod,
      amount: p.netPayment,
      positionId: p.positionId,
      paymentPrefId: p.paymentPrefId,
    };
  });

  return {
    objectId: distributionBatchId,
    objectType: PaymentObjectType.DistributionBatch,
    paymentDescription,
    payments,
    scheduledPaymentProcessDate: scheduledPaymentProcessISODate,
    makerNote: makerNote?.trim() || null,
  };
};
